import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { UntilDestroy } from '@ngneat/until-destroy';
import { VehicleAddressSection } from 'enums/vehicle-address-section';
import { PageRouteMapKeys, PageRouteMap } from 'shared/base/generics/models/page-route-map';
import { Route } from 'sharedStores/cms/models/route';
import { AddressVehicleNavigation } from 'models/address-vehicle-navigation';
import { selectPageRouteMap } from 'sharedStores/cms/cms.selectors';
import { selectQuoteFlowType, selectRiskAddress } from 'stores/quote/quote.selectors';
import { selectVehicleState } from 'stores/vehicle/vehicle.selectors';

/**
 * Comprehensive service for handling all navigation logic related to risk address routing,
 * including vehicle address section tracking, route conversion, and conditional page activation.
 * Handles both the navigation state management and the conditional routing logic for car insurance flows.
 */
@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class RiskAddressNavigationService {
    public constructor(private readonly store$: Store) {}
    // Address sections stack for tracking navigation history
    // [0] = Latest Section, [1] = Previous Section, [2] = Oldest Section
    private addressSections: (VehicleAddressSection | null)[] = [null, null, null];

    // Store signals for reactive state management
    private readonly pageRouteMap = this.store$.selectSignal(selectPageRouteMap);
    private readonly quoteFlowType = this.store$.selectSignal(selectQuoteFlowType);
    private readonly riskAddress = this.store$.selectSignal(selectRiskAddress);
    private readonly vehicleState = this.store$.selectSignal(selectVehicleState);

    /**
     * Converts a VehicleAddressSection to the corresponding Route from PageRouteMap
     * @param section - The vehicle address section to convert
     * @param pageRouteMap - The page route map containing all routes
     * @returns The corresponding Route object or null if not found
     */
    convertAddressSectiontoRoute(
        section: VehicleAddressSection,
        pageRouteMap: PageRouteMap,
    ): Route | null {
        const routeKey = this.getRouteKeyForSection(section);
        if (!routeKey) {
            return null;
        }
        return pageRouteMap[routeKey];
    }

    /**
     * Gets the PageRouteMapKeys for a given VehicleAddressSection
     * @param section - The vehicle address section to convert
     * @returns The corresponding PageRouteMapKeys or null if not found
     */
    getRouteKeyForSection(section: VehicleAddressSection): PageRouteMapKeys | null {
        switch (section) {
            case VehicleAddressSection.ResidentialAddressPage:
                return PageRouteMapKeys.ResidentialAddress;
            case VehicleAddressSection.CarParkedHomePage:
                return PageRouteMapKeys.CarParkedAddressHome;
            case VehicleAddressSection.CarParkedWorkPage:
                return PageRouteMapKeys.CarAddressSuburbWork;
            case VehicleAddressSection.CarParkedHomeMethodPage:
                return PageRouteMapKeys.CarParkedMethodHome;
            case VehicleAddressSection.CarParkedWorkMethodPage:
                return PageRouteMapKeys.CarParkedMethodWork;
            default:
                return null;
        }
    }

    /**
     * Sets the current vehicle address section and maintains navigation history
     * Used in residential address, car parked address work and car parked address home pages
     * @param section - The vehicle address section to set
     */
    setVehicleAddressSection(section: VehicleAddressSection): void {
        this.addressSections[2] = this.addressSections[1]; // OldestSection
        this.addressSections[1] = this.addressSections[0]; // Previous Section
        this.addressSections[0] = section; // Latest Section
    }

    /**
     * Gets the current vehicle address section
     * @returns The current vehicle address section
     */
    getVehicleAddressSection(): VehicleAddressSection | null {
        return this.addressSections[0];
    }

    /**
     * Determines navigation strategy based on address section history
     * Handles backward navigation detection and section routing logic
     * @returns AddressVehicleNavigation object with navigation details
     */
    getVehicleNavigation(): AddressVehicleNavigation {
        // Check if all three address sections are identical (indicates backward navigation)
        // This would happen if the user were to keep going forward and backward to the same page with risk address inbetween (unlikely but it does happen)
        // However if such is the case - have them start over at the residential address page
        if (
            this.addressSections[0] !== null &&
            this.addressSections.every((section, i, arr) => section === arr[0])
        ) {
            this.addressSections = [null, null, null]; // Reset the stack since we are starting over from Residential Address
            return {
                isBackwardNavigation: true,
                sectionToNavigateTo: VehicleAddressSection.ResidentialAddressPage,
                sectionToStoreAddress: null,
            };
        }

        // The below logic is used to determine if we went forward then further backward
        // E.g. Residential -> Risk Page -> CarParkedHome -> CarParkedHome Detail -> CarParkedHome -> Risk Page
        // Our addressSections array would look like this:
        // [CarParkedHomePage, CarParkedHomePage, ResidentialAddressPage]
        if (
            this.addressSections[0] == this.addressSections[1] &&
            this.addressSections[0] != this.addressSections[2]
        ) {
            const vehicleSectionToStoreAddress =
                this.addressSections[2] != null ? this.addressSections[2] : this.addressSections[1];

            const sectionToNavigateTo = this.getSectionToNavigateTo(vehicleSectionToStoreAddress);
            const addressVehicleNavigation = {
                isBackwardNavigation: true,
                sectionToStoreAddress: vehicleSectionToStoreAddress,
                sectionToNavigateTo: sectionToNavigateTo,
            };
            //To prevent weird behaviour -  we keep only the recent address section that was visited and clear the older ones
            this.addressSections[2] = null;
            this.addressSections[1] = null;
            return addressVehicleNavigation;
        }

        // If none of the above - then we move forward and let the standard navigation resume
        return {
            isBackwardNavigation: false,
            sectionToStoreAddress: this.addressSections[0],
            sectionToNavigateTo: this.getSectionToNavigateTo(this.addressSections[0]),
        };
    }

    /**
     * Determines the next section to navigate to based on the provided vehicle address section.
     *
     * @param vehicleSectionToStoreAddress - The current section of the vehicle address.
     * @returns The next {@link VehicleAddressSection} to navigate to, or `null` if there is no subsequent section.
     */
    getSectionToNavigateTo(
        vehicleSectionToStoreAddress: VehicleAddressSection,
    ): VehicleAddressSection | null {
        if (vehicleSectionToStoreAddress == VehicleAddressSection.ResidentialAddressPage) {
            return VehicleAddressSection.CarParkedHomePage;
        } else if (vehicleSectionToStoreAddress == VehicleAddressSection.CarParkedHomePage) {
            return VehicleAddressSection.CarParkedHomeMethodPage;
        } else if (vehicleSectionToStoreAddress == VehicleAddressSection.CarParkedWorkPage) {
            return VehicleAddressSection.CarParkedWorkMethodPage;
        }

        return null;
    }
}
